import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { OrganizationProfile } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { MediaIdField } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import {
  useOrganizationProfile,
  useUpdateOrganizationProfile,
} from '../hooks/use-organization-profile';
import {
  fromOrganizationProfile,
  organizationProfileSchema,
  toOrganizationProfileUpdate,
  type OrganizationProfileValues,
} from '../schemas/organization-profile-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'logoMediaId',
  'contactEmail',
  'contactPhone',
  'website',
]);

function ProfileForm({ profile, readOnly }: { profile: OrganizationProfile; readOnly: boolean }) {
  const { t } = useTranslation(['organization', 'common']);
  const message = useErrorMessage();
  const update = useUpdateOrganizationProfile();
  const form = useForm<OrganizationProfileValues>({
    resolver: useI18nResolver(organizationProfileSchema),
    defaultValues: fromOrganizationProfile(profile),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      const saved = await update.mutateAsync(toOrganizationProfileUpdate(values));
      form.reset(fromOrganizationProfile(saved));
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  const text = (
    name: 'name' | 'contactEmail' | 'contactPhone' | 'website',
    props: { type?: string; placeholder?: string } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`organization:profile.${name}`)}</FormLabel>
          <FormControl>
            <Input {...props} {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label={t('organization:profile.title')}
      >
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        {readOnly ? (
          <p className="text-muted-foreground text-sm">{t('organization:profile.ownersOnly')}</p>
        ) : null}
        <fieldset disabled={readOnly} className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {text('name')}
            {text('website', { type: 'url', placeholder: 'https://' })}
            {text('contactEmail', { type: 'email' })}
            {text('contactPhone', { type: 'tel' })}
          </div>
          <FormField
            control={form.control}
            name="logoMediaId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('organization:profile.logo')}</FormLabel>
                <MediaIdField
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  label={t('organization:profile.logoLabel')}
                  disabled={readOnly}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        {readOnly ? null : (
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}

/** Name, logo and contact details of the organisation; owners edit, everyone with org.read sees. */
export function OrganizationProfileCard() {
  const { t } = useTranslation('organization');
  const { can } = usePermissions();
  const query = useOrganizationProfile(can('org.read'));
  if (!can('org.read')) return null;
  return (
    <FormSection
      title={t('profile.title')}
      description={t('profile.description')}
      className="max-w-3xl"
      id={SECTION_ANCHORS.organizationProfile}
      tour="org.settings.profile"
    >
      {query.isPending ? (
        <Skeleton className="h-40" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <ProfileForm key={query.data.updatedAt} profile={query.data} readOnly={!can('org.write')} />
      )}
    </FormSection>
  );
}
